import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useLoadData } from '../../../../hooks/useLoadData';
import { useEffect, useState } from 'react';
import { ApiResponse } from '../../../../models/api-response';
import { proctorResultApiKeyStore } from '../../../../api/proctor-result-api-key-store';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { proctorColumns } from './grid-columns';
import { useSaveData } from '../../../../hooks/useSaveData';
import { ConfirmationDialog } from './confirmation.dialog';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useAuthorize } from '../../../../hooks/use-authorize';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

interface SelectProctorResultDialog {
  projectID: number;
  unitID: number;
  proctorResults: any[];
  onClosed: () => void;
}
export const SelectProctorResultDialog = ({
  projectID,
  unitID,
  proctorResults,
  onClosed,
}: SelectProctorResultDialog) => {
  const [selectedProcotor, setSelectedProcotor] = useState<any[]>(proctorResults ?? []);
  const [completeData, setCompleteData] = useState<any[]>();
  const [disabledButton, setDisabledButton] = useState<boolean>(true);
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
    proctorResultApiKeyStore.getProjectProctorResults(projectID)
  );

  const { isLoading: isMappedProctorLoading, mutateAsync } = useSaveData<any>(
    proctorResultApiKeyStore.mapProctorResult(),
    () => proctorResultApiKeyStore.getUnitProctorResults(unitID)
  );

  useEffect(() => {
    setDisabledButton(selectedProcotor?.length > 0 ? false : true);
  }, [selectedProcotor]);

  useEffect(() => {
    if (data) {
      const updatedData = data?.data.map((d) => ({
        ...d,
        isSelected: proctorResults?.some((s) => s.proctorResultsID === d.proctorResultsID),
      }));
      setCompleteData(updatedData);
    }
  }, [data]);

  const handleSelectedItem = (values: any[]) => {
    setSelectedProcotor(values);
  };

  const handleDialog = () => {
    setConfirmDialog(true);
  };
  const handleReplaceProctor = async () => {
    setConfirmDialog(false);
    const setData = {
      proctorResultsID: [selectedProcotor[0]?.proctorResultsID],
      unitID: unitID,
    };
    await mutateAsync({ data: setData });
    onClosed();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={onClosed}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
        MAP PROCTOR RESULTS
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 3,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers style={styling.contentPadding} className="popupDilog">
        <Grid>
          <SiTrackGrid
            fileTitle='unit_proctor_result'
            dataItems={completeData}
            selectedField="isSelected"
            dataItemKey="proctorResultsID"
            columns={proctorColumns}
            selectionMode="single"
            onSelectedItems={handleSelectedItem}
            actionPermissions={{
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            isBusy={isLoading || isMappedProctorLoading}
          />
          <Grid item>
            <div className="popupFooter alignRight">
              <Button
                type={'submit'}
                variant="contained"
                disableElevation={true}
                style={styling.buttonStyle}
                disabled={isLoading || isMappedProctorLoading || disabledButton || isReadOnlyPermission}
                onClick={handleDialog}
              >
                OK
              </Button>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      {confirmDialog && (
        <ConfirmationDialog onContinue={handleReplaceProctor} onClosed={() => setConfirmDialog(false)} />
      )}
    </Dialog>
  );
};
